/**
 * Comprehensive ResizeObserver Error Fix for Ant Design v4
 * Fixes the "ResizeObserver loop completed with undelivered notifications" error
 */

// 1. Suppress error overlay in development
const suppressErrorOverlay = () => {
  const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay');
  const resizeObserverErrDivRoot = document.getElementById('webpack-dev-server-client-overlay-div');
  
  if (resizeObserverErrDiv) {
    resizeObserverErrDiv.setAttribute('style', 'display: none !important');
  }
  if (resizeObserverErrDivRoot) {
    resizeObserverErrDivRoot.setAttribute('style', 'display: none !important');
  }
};

// 2. Override window.onerror to catch ResizeObserver errors
const originalWindowError = window.onerror;
window.onerror = function(message, source, lineno, colno, error) {
  // Suppress ResizeObserver errors
  if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
    suppressErrorOverlay();
    return true; // Prevent default error handling
  }
  
  // Call original error handler for other errors
  if (originalWindowError) {
    return originalWindowError.call(this, message, source, lineno, colno, error);
  }
  return false;
};

// 3. Suppress error event listener
window.addEventListener('error', (event: ErrorEvent) => {
  if (
    event.message === 'ResizeObserver loop completed with undelivered notifications.' ||
    event.message === 'ResizeObserver loop limit exceeded' ||
    (event.message && event.message.includes('ResizeObserver'))
  ) {
    suppressErrorOverlay();
    event.stopImmediatePropagation();
    event.preventDefault();
  }
});

// 4. Suppress unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver')) {
    suppressErrorOverlay();
    event.preventDefault();
  }
});

// 5. Debounce ResizeObserver to prevent loops
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const OriginalResizeObserver = window.ResizeObserver;
  
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback: any) {
      let timeout: number | null = null;
      
      const debouncedCallback = (entries: any, observer: any) => {
        if (timeout) {
          clearTimeout(timeout);
        }
        
        timeout = window.setTimeout(() => {
          try {
            callback(entries, observer);
          } catch (error) {
            // Silently catch ResizeObserver errors
            if (error instanceof Error && !error.message.includes('ResizeObserver')) {
              console.error('ResizeObserver error:', error);
            }
          }
        }, 16); // ~60fps (requestAnimationFrame interval)
      };
      
      super(debouncedCallback);
    }
  };
}

// 6. Suppress console errors for ResizeObserver
const originalConsoleError = console.error;
console.error = (...args: any[]) => {
  if (
    typeof args[0] === 'string' &&
    (args[0].includes('ResizeObserver loop') ||
     args[0].includes('Warning: Can\'t perform a React state update on an unmounted component') ||
     args[0].includes('Warning: Can\'t perform a React state update on a component that is not yet mounted'))
  ) {
    return; // Suppress these errors
  }
  originalConsoleError.apply(console, args);
};

// 7. Suppress console warnings for ResizeObserver
const originalConsoleWarn = console.warn;
console.warn = (...args: any[]) => {
  if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
    return; // Suppress ResizeObserver warnings
  }
  originalConsoleWarn.apply(console, args);
};

// 8. Add requestAnimationFrame wrapper for better performance
if (typeof window !== 'undefined') {
  let rafId: number | null = null;
  
  const observer = new MutationObserver(() => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    
    rafId = requestAnimationFrame(() => {
      suppressErrorOverlay();
    });
  });
  
  // Observe DOM changes to catch dynamically added error overlays
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

export {};

